import React from 'react'
import { Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material'
import * as datefns from 'date-fns'
import { EnrichedIssueEntity } from '../../../types/IssueEntity'
import { getSummarizedFeeLevels } from '../utils'
import { formatDateYYYYMMDD, formatNumber, formatPercentage } from '../../../packages/utils/numberFormatters'
import { useTranslation } from 'react-i18next'

const calcTotalRightsCounts = ({
    totalOfferedUnits,
    unitsReceived,
    rightsRequired,
}: {
    [key: string]: number | null
}) => {
    if (totalOfferedUnits === null || unitsReceived === null || rightsRequired === null) return null

    return (totalOfferedUnits / unitsReceived) * rightsRequired
}

const formatVwapPercentage = ({ hasShares, locale, vwap, rightsRequired, unitsReceived, unitSubPrice }) => {
    const vwapPercentage =
        vwap === null || rightsRequired === null || unitsReceived === null || unitSubPrice === null
            ? null
            : (vwap * rightsRequired) / unitsReceived / unitSubPrice

    if (vwapPercentage === null) return '-'
    if (parseFloat(unitSubPrice) === 0) return '-'

    return (
        formatPercentage(vwapPercentage * 100, { decimals: 1, locale }) +
        ' av priset per ' +
        (hasShares ? 'ny aktie' : 'unit')
    )
}

const formatQuantityPercentage = ({ locale, quantity, sumTotalRightsExclCommitment }) => {
    const quantityPercentage =
        quantity !== null && sumTotalRightsExclCommitment !== null
            ? (quantity / sumTotalRightsExclCommitment) * 100
            : null

    if (quantityPercentage === null) return '-'

    return (
        formatPercentage(quantityPercentage, {
            decimals: 0,
            locale,
        }) + ' av tillgängliga rätter'
    )
}

type TradingStatsRightsProps = Pick<
    EnrichedIssueEntity,
    | 'approxCommitment'
    | 'unitSubPrice'
    | 'totalOfferedUnits'
    | 'unitsReceived'
    | 'rightsRequired'
    | 'rightsEndDate'
    | 'rightsStartDate'
    | 'investors'
> & {
    endDateQuantity: null | number
    endDateVwap: null | number
    hasShares: boolean
    tradeQuantity: null | number
    vwap: null | number
    totalOfferingSize: number | null
}

function TradingStatsRights({
    approxCommitment,
    endDateQuantity,
    endDateVwap,
    hasShares,
    totalOfferedUnits,
    unitsReceived,
    rightsRequired,
    rightsEndDate,
    rightsStartDate,
    tradeQuantity,
    investors,
    unitSubPrice,
    vwap,
    totalOfferingSize,
}: TradingStatsRightsProps) {
    const { i18n } = useTranslation()
    const locale = i18n.language

    if (!rightsStartDate) {
        return 'Rätterna har inte börjat handlas än.'
    }

    if (new Date() < new Date(rightsStartDate)) {
        return 'Rätterna börjar handlas den ' + formatDateYYYYMMDD(rightsStartDate)
    }

    const filteredInvestors = investors.filter((p) => !p.isSeller)
    const sumTotalRights = calcTotalRightsCounts({ totalOfferedUnits, unitsReceived, rightsRequired })
    const sumSubscriptionCommitmentInPercentage =
        getSummarizedFeeLevels(filteredInvestors, totalOfferingSize).find((l) => l.feeLevel === 0)
            ?.amountInPercentage ?? approxCommitment
    const isOngoing =
        rightsEndDate === null ? true : datefns.startOfDay(new Date(rightsEndDate)) >= datefns.startOfDay(new Date())

    const sumTotalRightsExclCommitment =
        sumTotalRights !== null && sumSubscriptionCommitmentInPercentage !== null
            ? ((100 - sumSubscriptionCommitmentInPercentage) / 100) * sumTotalRights
            : sumTotalRights

    return (
        <TableContainer>
            <Table>
                <TableBody>
                    <TableRow>
                        <TableCell>Totalt antal rätter</TableCell>
                        <TableCell>
                            {sumTotalRights === null ? '-' : formatNumber(sumTotalRights, { locale, decimals: 0 })}
                        </TableCell>
                        <TableCell />
                    </TableRow>
                    <TableRow>
                        <TableCell>Ungefärligt antal rätter exkl TÅ</TableCell>
                        <TableCell>
                            {sumTotalRightsExclCommitment === null
                                ? '-'
                                : formatNumber(sumTotalRightsExclCommitment, {
                                      locale,
                                      decimals: 0,
                                  })}
                        </TableCell>
                        <TableCell />
                    </TableRow>

                    <TableRow>
                        <TableCell>Omsatta rätter {isOngoing ? 'hittills' : 'totalt'}</TableCell>
                        <TableCell>
                            {tradeQuantity === null ? '-' : formatNumber(tradeQuantity, { locale, decimals: 0 })}
                        </TableCell>
                        <TableCell>
                            {formatQuantityPercentage({
                                quantity: tradeQuantity,
                                sumTotalRightsExclCommitment,
                                locale,
                            })}
                        </TableCell>
                    </TableRow>

                    <TableRow>
                        <TableCell>Omsatta rätter sista handelsdagen</TableCell>
                        {isOngoing && (
                            <TableCell colSpan={2}>Sista dagen: {formatDateYYYYMMDD(rightsEndDate)}</TableCell>
                        )}
                        {!isOngoing && (
                            <>
                                <TableCell>
                                    {endDateQuantity === null
                                        ? '-'
                                        : formatNumber(endDateQuantity, { locale, decimals: 0 })}
                                </TableCell>
                                <TableCell>
                                    {formatQuantityPercentage({
                                        quantity: endDateQuantity,
                                        sumTotalRightsExclCommitment,
                                        locale,
                                    })}
                                </TableCell>
                            </>
                        )}
                    </TableRow>

                    <TableRow>
                        <TableCell>Rätternas VWAP totalt</TableCell>
                        <TableCell>
                            {vwap === null ? '-' : formatNumber(vwap, { minDecimals: 2, maxDecimals: 5, locale })}
                        </TableCell>
                        <TableCell>
                            {formatVwapPercentage({
                                hasShares,
                                unitsReceived,
                                unitSubPrice,
                                locale,
                                rightsRequired,
                                vwap,
                            })}
                        </TableCell>
                    </TableRow>

                    <TableRow>
                        <TableCell>Rätternas VWAP sista handelsdagen</TableCell>
                        {isOngoing && (
                            <TableCell colSpan={2}>Sista dagen: {formatDateYYYYMMDD(rightsEndDate)}</TableCell>
                        )}
                        {!isOngoing && (
                            <>
                                <TableCell>
                                    {endDateVwap === null
                                        ? '-'
                                        : formatNumber(endDateVwap, { minDecimals: 2, maxDecimals: 5, locale })}
                                </TableCell>
                                <TableCell>
                                    {formatVwapPercentage({
                                        hasShares,
                                        unitsReceived,
                                        unitSubPrice,
                                        locale,
                                        rightsRequired,
                                        vwap: endDateVwap,
                                    })}
                                </TableCell>
                            </>
                        )}
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default TradingStatsRights
